import { useRouter } from "next/router"
import { useEffect, useState } from "react"
import { BlogCard, BlogSkeletonLoader, CategoryFilter, Pagination, SearchBar } from "../components/blogs"
import { FooterSection, HeaderSection } from "../components/layout"
import { Loader } from "../components/common"
import styles from "../styles/components/blogs/Blogs.module.css"
import { blogLandingPage, fetchBlogs, fetchBlogsPage, fetchConfigPage } from "../utils/api"
import SEOHead, { getPageSEO } from "../utils/helpers/seo"
import { useBlogSearch } from "../utils/hooks/useBlogSearch"
import { useDarkMode } from "../utils/hooks/useDarkMode"

export default function BlogsPage({ initialData, error }) {
  const router = useRouter()

  const [strapiData, setStrapiData] = useState(initialData)
  const [categories, setCategories] = useState(initialData?.initialCategories || ["All"])

  // Dark mode hook
  const { isDarkMode, isThemeInitialized } = useDarkMode()

  // Use custom hook for blog search functionality
  const {
    blogs,
    isLoading,
    currentPage,
    totalPages,
    totalItems,
    searchTerm,
    selectedCategory,
    handleSearch,
    handleCategorySelect,
    handlePageChange,
    getQueryParams
  } = useBlogSearch(initialData, 9)

  // Process categories from blogs data
  const processCategories = (blogsData) => {
    const uniqueCategories = new Set()
    uniqueCategories.add("All")

    blogsData.forEach((blog) => {
      if (blog.categories && blog.categories.length > 0) {
        blog.categories.forEach((category) => {
          if (category.name && category.name !== "Uncategorized") {
            uniqueCategories.add(category.name)
          }
        })
      }
    })

    return Array.from(uniqueCategories)
  }

  useEffect(() => {
    if (initialData?.categoriesData?.data) {
      const processedCategories = processCategories(initialData?.categoriesData?.data)
      setCategories(processedCategories)
    }
  }, [initialData?.categoriesData?.data])

  // Priority 1: Show error if there's an error
  if (error) {
    return <Loader type="error" message="Oops! Something Went Wrong" description="Please wait a moment and try again. We'll be back up shortly!" fullPage={true} action={() => window.location.reload()} actionText="Refresh Page" />;
  }

  // Priority 2: Show loading if no data yet
  if (!initialData) {
    return <Loader type="loading" message="Loading blogs..." description="Please wait while we fetch the latest content." fullPage={true} />;
  }

  // Extract content from Strapi data when available
  const configData = strapiData?.configData || {}
  const header = configData.header || {}
  const footer = configData.footer || {}
  const pageData = strapiData?.blogLandingPageData || {}

  // Priority 3: Show empty state if no blogs configuration exists
  if (!pageData || Object.keys(pageData).length === 0) {
    return <Loader type="empty" message="No Blog Configuration" description="The blog section hasn't been set up yet. Please check back later." fullPage={true} action={() => router.push('/')} actionText="Refresh Page" />;
  }

  // Extract Hero Data from Strapi Data
    const heroSection = strapiData?.blogLandingPageData?.data?.HeroSection ?? strapiData?.pageData?.HeroSection

  // Generate SEO data for this blogs page
  const seoData = getPageSEO(strapiData?.blogLandingPageData?.data, configData, strapiData.pageUrl);
  const blogLandingSEO = initialData?.blogLandingPageData?.data?.seo || {};

  // Build pagination URLs for rel="prev" and rel="next"
  const buildPaginationUrl = (page) => {
    const params = new URLSearchParams()
    if (searchTerm) params.set('search', searchTerm)
    if (selectedCategory && selectedCategory !== 'All') params.set('category', selectedCategory)
    if (page > 1) params.set('page', page.toString())
    const queryString = params.toString()
    const baseUrl = configData?.siteUrl || process.env.NEXT_PUBLIC_BASE_URL || 'https://hirego.co.uk'
    return queryString ? `${baseUrl}/blogs?${queryString}` : `${baseUrl}/blogs`
  }

  const prevUrl = currentPage > 1 ? buildPaginationUrl(currentPage - 1) : null
  const nextUrl = currentPage < totalPages ? buildPaginationUrl(currentPage + 1) : null

  return (
    <>


      <SEOHead 
        pageData={pageData}
        configData={configData}
        pageUrl={strapiData?.pageUrl}
        customSEO={{
          title: blogLandingSEO?.metaTitle || seoData.title,
          description: blogLandingSEO?.metaDescription || seoData.description,
          canonical: blogLandingSEO?.canonicalURL || seoData.canonical,
          prev: prevUrl,
          next: nextUrl,
          openGraph: {
            title: blogLandingSEO?.metaSocial?.[0]?.title || blogLandingSEO?.metaTitle || seoData.title,
            description: blogLandingSEO?.metaSocial?.[0]?.description || blogLandingSEO?.metaDescription || seoData.description,
            image: blogLandingSEO?.metaImage?.url || seoData.openGraph.images[0]?.url,
            type: 'website',
            url: blogLandingSEO?.canonicalURL || seoData.canonical,
          },
          twitter: {
            title: blogLandingSEO?.metaSocial?.[1]?.title || blogLandingSEO?.metaTitle || seoData.title,
            description: blogLandingSEO?.metaSocial?.[1]?.description || blogLandingSEO?.metaDescription || seoData.description,
            image: blogLandingSEO?.metaImage?.url || seoData.twitter.images,
          },
          structuredData: blogLandingSEO?.structuredData || seoData.structuredData,
        }}
      />

      <HeaderSection data={header} pageUrl={strapiData?.pageUrl} phoneNumber={pageData?.EnhancedHeroSection?.phone} />

      <main className={styles.mainContent}>
        <section className={styles.heroSection}>
          <div className="tw-container !mt-[1rem]">
            {heroSection && (
              <div className={styles.heroContent}>
                <h1 className={styles.heroTitle}>{heroSection.title}</h1>
              </div>
            )}
            {/* Search Bar */}
            <SearchBar
                value={searchTerm}
                onChange={handleSearch}
              placeholder="Search blogs..."
            />

            {/* Categories */}
            <CategoryFilter
              categories={categories}
              selectedCategory={selectedCategory}
              onCategorySelect={handleCategorySelect}
            />
          </div>
        </section>

        {/* Blogs Content */}
        <section className={styles.blogsSection}>
          <div className="tw-container">
            {isLoading ? (
              <BlogSkeletonLoader count={9} />
            ) : blogs.length === 0 ? (
              <div className={styles.noBlogsMessage}>
                <div className={styles.noBlogsIcon}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                  </svg>
                </div>
                <h3>No Blogs Found</h3>
                <p>
                  We couldn&apos;t find any blogs matching your criteria. Try adjusting your search or browse all
                  categories.
                </p>
              </div>
            ) : (
              <>
                <div className={styles.blogsGrid}>
                  {blogs.map((blog, index) => (
                    <BlogCard 
                      key={blog.id} 
                      blog={blog} 
                      isFirstCard={index === 0}
                    />
                  ))}
                </div>

                {/* Pagination */}
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  queryParams={getQueryParams()}
                />
              </>
            )}
          </div>
        </section>
      </main>

      <FooterSection data={footer} />
    </>
  )
}

// Server-side data fetching
export async function getServerSideProps(context) {
  try {
    const { page, search, category } = context.query
    const currentPage = page ? Number.parseInt(page, 10) : 1

    // Fetch all required data in parallel
    const [pageDataResponse, configData, blogsResponse, blogLandingPageData] = await Promise.all([
      fetchBlogsPage(),
      fetchConfigPage(),
      fetchBlogs({ 
        page: currentPage, 
        pageSize: 9,
        search: search || undefined,
        category: category !== 'All' ? category : undefined
      }),
      blogLandingPage()
    ])

    // Check if essential data is missing (return empty structure, not null)
    if (!configData || !blogsResponse) {
      return {
        props: {
          initialData: {
            configData: configData || {},
            blogLandingPageData: blogLandingPageData || {},
            categoriesData: pageDataResponse || {},
            pageUrl: page === 1 ? '/blogs' : `/blogs?page=${page}`,
            initialBlogs: { data: [], meta: { pagination: { page: 1, pageCount: 1, total: 0 } } },
            initialCategories: [],
          },
          error: null,
        },
      }
    }

    // Return valid data
    return {
      props: {
        initialData: {
          configData: configData,
          blogLandingPageData: blogLandingPageData,
          categoriesData: pageDataResponse,
          pageUrl: page === 1 ? '/blogs' : `/blogs?page=${page}`,
          initialBlogs: blogsResponse,
          initialCategories: [],
        },
        error: null,
      },
    }
  } catch (error) {
    // Return error (with null initialData)
    return {
      props: {
        initialData: null,
        error: error.message || 'An unexpected error occurred while loading blogs.',
      },
    }
  }
}
